import { FC, Fragment, useCallback, useMemo } from 'react';

import { kstShiftDay } from '@shared/utils/kstTime.util';

import { AnalyticsDayLabels, AnalyticsDayPoint } from './analyticsDay.types';
import { DistributionRow } from './AnalyticsDistributionRow';
import { describeDistribution } from './analyticsDistribution.util';
import { baselineIndexTone, formatBaselineIndex } from './AnalyticsShared';

//* 달력. 기존 답례품 발인일 통계 달력과 같은 방식 —
//* 라이브러리 없이 grid + gap-px로 칸을 그리고, 배경 알파로 히트맵을 낸다.
//* 부고/화환/답례품 탭이 공용으로 쓴다 (지표 이름은 labels로 받는다).
//* 7열이 아니라 8열이고 마지막 열은 그 주의 합계다 — 오른쪽 빈 공간을 쓰는 자리.

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const HEAT_RGB = '99, 102, 241';

const CELL_HEIGHT = 'min-h-[76px] sm:min-h-[100px]';

const weekdayColor = (weekday: number) =>
  weekday === 0 ? 'text-red-500' : weekday === 6 ? 'text-blue-500' : 'text-gray-500';

/**
 * 'YYYY-MM-DD'의 요일.
 * 브라우저 시간대에 흔들리지 않도록 UTC 자정으로 해석한다 (문자열은 이미 KST 달력 날짜).
 */
const weekdayOf = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay();

/** 'YYYY-MM-DD' → 'M/D' */
const shortDate = (date: string) =>
  `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;

interface MonthGroup {
  ym: string;
  days: AnalyticsDayPoint[];
}

const groupByMonth = (days: AnalyticsDayPoint[]): MonthGroup[] => {
  const map = new Map<string, AnalyticsDayPoint[]>();
  days.forEach((day) => {
    const ym = day.date.slice(0, 7);
    const bucket = map.get(ym);
    if (bucket) bucket.push(day);
    else map.set(ym, [day]);
  });
  return Array.from(map.entries()).map(([ym, monthDays]) => ({ ym, days: monthDays }));
};

/** 그 날짜가 속한 주의 일요일 */
const sundayOf = (date: string) => kstShiftDay(date, -weekdayOf(date));

interface WeekAgg {
  /** 주의 일요일 (달이 갈려도 같은 주면 같은 키) */
  key: string;
  value: number;
  subValue: number;
  /** 조회 구간 안에서 실제로 잡힌 첫/마지막 날 */
  from: string;
  to: string;
  /** 조회 구간이 그 주를 다 덮지 못했는지 (7일 미만) */
  isPartial: boolean;
}

/**
 * 주간 합계는 **달 경계와 무관하게** 조회 구간 전체에서 계산한다.
 * 달마다 따로 접으면 월말·월초에 걸친 주가 반토막 나서
 * "8/1 한 칸만 잡혀 매출이 급락"하는 것처럼 보인다 (실사고).
 * 구간이 주를 다 못 덮는 경우(조회 시작·끝 주)만 '부분'으로 표시한다.
 */
const buildWeekAggregates = (days: AnalyticsDayPoint[]): Map<string, WeekAgg> => {
  const map = new Map<string, WeekAgg>();
  const covered = new Map<string, number>();

  days.forEach((day) => {
    const key = sundayOf(day.date);
    const agg = map.get(key);
    if (!agg) {
      map.set(key, {
        key,
        value: day.value,
        subValue: day.subValue ?? 0,
        from: day.date,
        to: day.date,
        isPartial: true,
      });
      covered.set(key, 1);
    } else {
      agg.value += day.value;
      agg.subValue += day.subValue ?? 0;
      if (day.date < agg.from) agg.from = day.date;
      if (day.date > agg.to) agg.to = day.date;
      covered.set(key, (covered.get(key) ?? 0) + 1);
    }
  });

  map.forEach((agg, key) => {
    agg.isPartial = (covered.get(key) ?? 0) < 7;
  });
  return map;
};

/** 한 달치 날짜를 일요일 시작 7칸 행으로 자른다 (합계는 위 주 집계에서 가져온다) */
const buildMonthRows = (days: AnalyticsDayPoint[]): (AnalyticsDayPoint | null)[][] => {
  const first = days[0];
  if (!first) return [];

  const slots: (AnalyticsDayPoint | null)[] = [];
  for (let i = 0; i < weekdayOf(first.date); i += 1) slots.push(null);
  days.forEach((day) => slots.push(day));
  while (slots.length % 7 !== 0) slots.push(null);

  const rows: (AnalyticsDayPoint | null)[][] = [];
  for (let i = 0; i < slots.length; i += 7) rows.push(slots.slice(i, i + 7));
  return rows;
};

const monthLabel = (ym: string) => {
  const [year, month] = ym.split('-');
  return `${year}년 ${Number(month)}월`;
};

/** 큰 수는 칸을 넘치므로 만 단위로 줄여 쓴다 */
const compact = (value: number): string =>
  value >= 100_000_000
    ? `${Math.round(value / 10_000_000) / 10}억`
    : value >= 10_000
    ? `${Math.round(value / 1_000) / 10}만`
    : value.toLocaleString();

/** 0으로 나누기 없이 비율 표기 */
const rateLabel = (part: number, whole: number): string =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '-';

/** 월간 통계 칩 */
const MonthChip: FC<{
  label: string;
  value: string;
  tone?: 'indigo' | 'emerald' | 'gray';
}> = ({ label, value, tone = 'gray' }) => {
  const toneMap = {
    indigo: 'bg-indigo-50 text-indigo-800',
    emerald: 'bg-emerald-50 text-emerald-800',
    gray: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`rounded-lg px-3 py-1.5 text-14 font-semibold tabular-nums ${toneMap[tone]}`}
    >
      <span className="mr-1 font-normal opacity-75">{label}</span>
      {value}
    </span>
  );
};

/** 부분값 비율 (0~1). 주 지표가 0이면 비율이 없다 */
const dayRateOf = (day: AnalyticsDayPoint): number | null =>
  day.value > 0 ? (day.subValue ?? 0) / day.value : null;

export const AnalyticsCalendar: FC<{
  days: AnalyticsDayPoint[];
  labels: AnalyticsDayLabels;
  onDayClick: (date: string) => void;
  /** 주간 합계 칸 클릭 — 그 주 전체 목록. 없으면 주간 칸을 누를 수 없다 */
  onWeekClick?: (from: string, to: string, label: string) => void;
  /** 안내 문구 (기본: 날짜를 누르면 목록) */
  hint?: string;
  /**
   * 칸마다 부분값 비율(subValue ÷ value)을 게이지와 함께 표시한다.
   * "그날 상가 중 몇 %가 샀나"처럼 **합계가 아니라 비율이 본체**인 화면용.
   */
  showRate?: boolean;
  /**
   * 히트맵 농도 기준. 기본은 주 지표 크기('value')지만, 비율이 본체인 화면은
   * 'rate'로 두어야 "많이 발인한 날"이 아니라 "잘 판 날"이 진하게 보인다.
   */
  heatBy?: 'value' | 'rate';
  /**
   * 기준선 비율(0~1). 주면 칸마다 "평균 대비 95%"를 함께 낸다.
   * 비율 숫자만으론 그날이 좋은 날인지 나쁜 날인지 감이 안 오기 때문.
   */
  rateBaseline?: number | null;
  rateBaselineLabel?: string;
  /**
   * 이 수보다 적게 잡힌 날은 지수에 색을 칠하지 않는다.
   * 발인 1건짜리 날은 0% 아니면 200%로 튀어서 빨강/초록이 거짓 신호가 된다.
   */
  rateBaselineMinValue?: number;
}> = function AnalyticsCalendar({
  days,
  labels,
  onDayClick,
  onWeekClick,
  hint,
  showRate,
  heatBy = 'value',
  rateBaseline,
  rateBaselineLabel = '평균 대비',
  rateBaselineMinValue = 3,
}) {
  const months = useMemo(() => groupByMonth(days), [days]);

  const heatOf = useCallback(
    (day: AnalyticsDayPoint) => (heatBy === 'rate' ? dayRateOf(day) ?? 0 : day.value),
    [heatBy],
  );

  const heatMax = useMemo(() => {
    const max = days.reduce((acc, day) => Math.max(acc, heatOf(day)), 0);
    return max > 0 ? max : 1;
  }, [days, heatOf]);
  //* 주 합계는 달이 아니라 조회 구간 전체 기준 (월말·월초 걸친 주가 반토막 나지 않도록)
  const weekAggregates = useMemo(() => buildWeekAggregates(days), [days]);

  if (months.length === 0) return null;

  const titleOf = (day: AnalyticsDayPoint) =>
    `${day.date} · ${labels.value} ${day.value.toLocaleString()}${labels.unit}` +
    (labels.sub
      ? ` · ${labels.sub} ${(day.subValue ?? 0).toLocaleString()}${labels.unit}`
      : '') +
    (showRate ? ` · ${rateLabel(day.subValue ?? 0, day.value)}` : '') +
    (showRate && rateBaseline && day.value > 0
      ? ` · ${rateBaselineLabel} ${formatBaselineIndex(
          (day.subValue ?? 0) / day.value / rateBaseline,
        )}`
      : '') +
    (day.note ? ` · ${day.note}` : '');

  return (
    <div className="space-y-3">
      {months.map((month) => {
        const rows = buildMonthRows(month.days);
        const monthTotal = month.days.reduce((sum, day) => sum + day.value, 0);
        const monthSub = month.days.reduce((sum, day) => sum + (day.subValue ?? 0), 0);
        //* 평균·중앙값·최저/최다·편차 — 값이 있었던 날 기준
        const dist = describeDistribution(month.days);

        return (
          <div
            key={month.ym}
            //* 초광폭 화면에서 칸이 옆으로만 늘어지지 않도록 폭을 잡는다
            className="max-w-6xl rounded-lg bg-white p-3 shadow-smd"
          >
            {/* 월간 통계 — 작은 회색 한 줄이던 걸 칩으로 키웠다 */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-20 font-bold text-gray-900">{monthLabel(month.ym)}</p>
              <div className="flex flex-wrap gap-1.5">
                <MonthChip
                  label={labels.value}
                  value={monthTotal.toLocaleString()}
                  tone="indigo"
                />
                {labels.sub && (
                  <MonthChip
                    label={labels.sub}
                    value={`${monthSub.toLocaleString()} (${rateLabel(
                      monthSub,
                      monthTotal,
                    )})`}
                    tone="emerald"
                  />
                )}
              </div>
            </div>

            {dist && <DistributionRow stats={dist} labels={labels} format={compact} />}

            <div className="mt-2 grid grid-cols-8 gap-px">
              {WEEKDAY_LABELS.map((label, weekday) => (
                <div
                  key={label}
                  className={`py-1.5 text-center text-12 font-semibold ${weekdayColor(
                    weekday,
                  )}`}
                >
                  {label}
                </div>
              ))}
              <div className="py-1.5 text-center text-12 font-semibold text-gray-700">
                주간
              </div>
            </div>

            {/* overflow-hidden 금지 — 첫 줄 칸의 툴팁이 위로 뜬다 */}
            <div className="grid grid-cols-8 gap-px bg-gray-200 ring-1 ring-gray-200">
              {rows.map((row, rowIndex) => {
                //* 이 행이 속한 주 — 달이 갈려도 같은 주면 같은 집계를 본다
                const firstOfRow = row.find(
                  (slot): slot is AnalyticsDayPoint => slot !== null,
                );
                const week = firstOfRow
                  ? weekAggregates.get(sundayOf(firstOfRow.date))
                  : undefined;
                const weekDisabled = !onWeekClick || !week || week.value === 0;

                return (
                  <Fragment key={`${month.ym}-row-${rowIndex}`}>
                    {row.map((day, index) => {
                      if (!day) {
                        return (
                          <div
                            key={`${month.ym}-blank-${rowIndex}-${index}`}
                            className={`${CELL_HEIGHT} bg-gray-50`}
                          />
                        );
                      }
                      const empty = day.value === 0;
                      const alpha = empty ? 0 : 0.08 + 0.62 * (heatOf(day) / heatMax);
                      const dayRate = dayRateOf(day);
                      return (
                        <button
                          key={day.date}
                          type="button"
                          disabled={empty}
                          onClick={() => onDayClick(day.date)}
                          title={titleOf(day)}
                          className={`${CELL_HEIGHT} bg-white p-1.5 text-left align-top ${
                            empty
                              ? 'cursor-default'
                              : 'cursor-pointer hover:ring-2 hover:ring-indigo-400'
                          }`}
                          style={
                            alpha > 0
                              ? { backgroundColor: `rgba(${HEAT_RGB}, ${alpha})` }
                              : undefined
                          }
                        >
                          <p
                            className={`text-12 font-semibold ${weekdayColor(
                              weekdayOf(day.date),
                            )}`}
                          >
                            {Number(day.date.slice(8, 10))}
                          </p>
                          {!empty && (
                            <>
                              <p className="text-22 font-bold tabular-nums leading-7 text-gray-900">
                                {compact(day.value)}
                              </p>
                              {labels.sub && (
                                <p className="text-12 tabular-nums leading-4 text-gray-700">
                                  {labels.sub} {compact(day.subValue ?? 0)}
                                </p>
                              )}
                              {/* 비율이 본체인 화면 — 숫자만으론 칸끼리 비교가 안 돼 막대를 같이 그린다 */}
                              {showRate && dayRate !== null && (
                                <>
                                  <p className="text-14 font-bold tabular-nums leading-5 text-indigo-800">
                                    {rateLabel(day.subValue ?? 0, day.value)}
                                  </p>
                                  <span className="mt-0.5 block h-1.5 w-full overflow-hidden rounded-full bg-white/70">
                                    <span
                                      className="block h-full rounded-full bg-indigo-600"
                                      style={{ width: `${Math.min(100, dayRate * 100)}%` }}
                                    />
                                  </span>
                                  {!!rateBaseline && (
                                    <p
                                      className={`text-12 font-semibold tabular-nums leading-4 ${
                                        //* 표본이 적은 날은 지수가 튄다 — 색으로 단정하지 않는다
                                        day.value < rateBaselineMinValue
                                          ? 'text-gray-400'
                                          : baselineIndexTone(dayRate / rateBaseline)
                                      }`}
                                      title={
                                        day.value < rateBaselineMinValue
                                          ? `${labels.value} ${day.value}${labels.unit}뿐이라 지수가 크게 흔들립니다`
                                          : undefined
                                      }
                                    >
                                      {rateBaselineLabel}{' '}
                                      {formatBaselineIndex(dayRate / rateBaseline)}
                                    </p>
                                  )}
                                </>
                              )}
                              {day.note && (
                                <p className="text-11 tabular-nums leading-4 text-gray-600">
                                  {day.note}
                                </p>
                              )}
                            </>
                          )}
                        </button>
                      );
                    })}

                    {/* 주간 합계 — 날짜 칸과 배경을 달리해 한눈에 구분되게 */}
                    <button
                      type="button"
                      disabled={weekDisabled}
                      onClick={() =>
                        week &&
                        onWeekClick?.(
                          week.from,
                          week.to,
                          `${shortDate(week.from)}~${shortDate(week.to)} 주간`,
                        )
                      }
                      title={
                        week
                          ? `${week.from}~${week.to} 주간 합계 · ${
                              labels.value
                            } ${week.value.toLocaleString()}${
                              labels.sub
                                ? ` · ${labels.sub} ${week.subValue.toLocaleString()}`
                                : ''
                            }${week.isPartial ? ' (조회 구간에 걸친 부분 주)' : ''}`
                          : undefined
                      }
                      className={`${CELL_HEIGHT} bg-gray-50 p-1.5 text-left align-top ${
                        weekDisabled
                          ? 'cursor-default'
                          : 'cursor-pointer hover:ring-2 hover:ring-gray-400'
                      }`}
                    >
                      {week && (
                        <>
                          <p className="text-11 tabular-nums leading-4 text-gray-500">
                            {shortDate(week.from)}~{shortDate(week.to)}
                            {week.isPartial && (
                              <span className="ml-1 rounded bg-gray-200 px-1 not-italic text-gray-600">
                                부분
                              </span>
                            )}
                          </p>
                          {week.value > 0 && (
                            <>
                              <p className="text-22 font-bold tabular-nums leading-7 text-gray-900">
                                {compact(week.value)}
                              </p>
                              {labels.sub && (
                                <>
                                  <p className="text-12 tabular-nums leading-4 text-gray-700">
                                    {labels.sub} {compact(week.subValue)}
                                  </p>
                                  <p className="text-11 tabular-nums leading-4 text-emerald-700">
                                    {rateLabel(week.subValue, week.value)}
                                  </p>
                                  {/* 주 단위는 표본이 충분해 색을 그대로 믿어도 된다 */}
                                  {!!rateBaseline && (
                                    <p
                                      className={`text-11 font-semibold tabular-nums leading-4 ${baselineIndexTone(
                                        week.subValue / week.value / rateBaseline,
                                      )}`}
                                    >
                                      {rateBaselineLabel}{' '}
                                      {formatBaselineIndex(
                                        week.subValue / week.value / rateBaseline,
                                      )}
                                    </p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
      <p className="px-1 text-12 text-gray-500">
        {hint ?? '날짜를 누르면 그날 목록이 열립니다.'}{' '}
        {heatBy === 'rate'
          ? `색이 진할수록 ${labels.sub ?? ''}율이 높은 날입니다.`
          : '색이 진할수록 값이 큰 날입니다.'}
      </p>
    </div>
  );
};
